import normalizeToLowerStringArray from '~/utils/normalizeToLowerStringArray';

export enum RoleAccessStatus {
    Allowed = 'allowed',
    NeedsLogin = 'needs_login',
    Forbidden = 'forbidden'
}

export type RoleAccessAuth =
    | {
          profession?: string | null;
          groups?: string[] | null;
      }
    | null
    | undefined;

export function getRoleAccessKeys(auth: RoleAccessAuth): string[] {
    if (!auth) return [];

    return Array.from(
        new Set([...normalizeToLowerStringArray(auth.profession), ...normalizeToLowerStringArray(auth.groups)])
    );
}

/**
 * Everything about a caller that can change the outcome of
 * `getRoleAccessStatus`: whether they are logged in at all, and which role keys
 * they carry. Two callers with the same value therefore get the same
 * role-filtered response, which is what lets a cache key identify a *role*
 * rather than a user - see the `getKey` in
 * server/api/storyblok-proxy/index.get.ts.
 *
 * Order- and duplicate-independent, because `getRoleAccessKeys` dedupes and this
 * sorts. JSON rather than a joined string so that a role name containing the
 * separator cannot make two different role sets collide.
 */
export function getRoleAccessCacheKey(auth: RoleAccessAuth): string {
    // Distinct from a logged-in caller with no roles: `getRoleAccessStatus`
    // returns NeedsLogin for one and Forbidden for the other.
    if (!auth) return 'anonymous';

    return `roles:${JSON.stringify(getRoleAccessKeys(auth).sort())}`;
}

export function getRoleAccessStatus(allowedRolesInput: unknown, auth: RoleAccessAuth): RoleAccessStatus {
    const allowedRoles = normalizeToLowerStringArray(allowedRolesInput);
    const isLoggedIn = Boolean(auth);

    if (allowedRoles.length === 0) return RoleAccessStatus.Allowed;

    if (!isLoggedIn) return RoleAccessStatus.NeedsLogin;

    if (allowedRoles.includes('all')) {
        return RoleAccessStatus.Allowed;
    }

    const accessKeys = getRoleAccessKeys(auth);
    return allowedRoles.some((role) => accessKeys.includes(role))
        ? RoleAccessStatus.Allowed
        : RoleAccessStatus.Forbidden;
}
